export const MONTHS = {
  '0': 'January',
  '1': 'February',
  '2': 'March',
  '3': 'April',
  '4': 'May',
  '5': 'June',
  '6': 'July',
  '7': 'August',
  '8': 'September',
  '9': 'October',
  '10': 'November',
  '11': 'December',
}

export function formatMonthDay(isoString: string): string {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
  }).format(new Date(isoString))
}

export function formatMonthDayAndYear(isoString: string): string {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(isoString))
}

export const dayInMs = 1000 * 60 * 60 * 24

export function getNextMonth(): Date {
  const now = new Date()

  return new Date(now.getFullYear(), now.getMonth() + 1, 1)
}

export function getOneWeekAgoString(): string {
  const date = new Date()
  date.setDate(date.getDate() - 7)

  return date.toISOString()
}

export function getOneDayAgoString(): string {
  const date = new Date()
  date.setDate(date.getDate() - 1)

  return date.toISOString()
}

export function getNMonthsAgoTimestamp(n: number): number {
  const date = new Date()
  date.setMonth(date.getMonth() - n)
  return date.getTime()
}

export function isMoreThanOneHourAgo(timestamp: number): boolean {
  if (timestamp) {
    const diffInSeconds = Math.floor(Math.abs(Date.now() - timestamp) / 1000)
    return Math.floor(diffInSeconds / 60) > 60
  }
  return false
}

export function isSameDay(timestampInMs: number): boolean {
  if (timestampInMs) {
    const todaysDate = new Date()
    const inputDate = new Date(timestampInMs)

    return inputDate.setHours(0, 0, 0, 0) === todaysDate.setHours(0, 0, 0, 0)
  }
  return false
}

export function getTimeSinceInSeconds(timestampInMs: number): number {
  if (timestampInMs) {
    const diffInMilliseconds = Date.now() - timestampInMs
    if (diffInMilliseconds > 0) {
      return Math.floor(diffInMilliseconds / 1000)
    }
  }
  return 0
}

export function getMsAsMinutes(milliseconds: number): number {
  if (milliseconds) {
    return Math.floor(milliseconds / 1000 / 60)
  }
  return 0
}

export function getTimeBetweenTimestampsStr(
  a: number,
  b: number,
  abbreviated: boolean = true,
): string {
  // Let's install and use date-fns at some point instead of continuing to add
  // to this function. See https://date-fns.org/v4.1.0/docs/intlFormatDistance
  let strForTimestamp
  const diffInMilliseconds = Math.abs(b - a)
  const diffInSeconds = Math.floor(diffInMilliseconds / 1000)
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)
  const diffInWeeks = Math.floor(diffInDays / 7)
  const diffInMonths = Math.floor(diffInWeeks / 4)
  const diffInYears = Math.floor(diffInDays / 365)
  if (diffInYears > 0) {
    if (diffInYears === 1) {
      strForTimestamp = abbreviated ? `${diffInYears}y` : `${diffInYears} year`
    } else {
      strForTimestamp = abbreviated ? `${diffInYears}y` : `${diffInYears} years`
    }
  } else if (diffInMonths > 0) {
    if (diffInMonths === 1) {
      strForTimestamp = abbreviated
        ? `${diffInMonths}mo`
        : `${diffInMonths} month`
    } else {
      strForTimestamp = abbreviated
        ? `${diffInMonths}mo`
        : `${diffInMonths} months`
    }
  } else if (diffInWeeks > 0) {
    if (diffInWeeks === 1) {
      strForTimestamp = abbreviated ? `${diffInWeeks}w` : `${diffInWeeks} week`
    } else {
      strForTimestamp = abbreviated ? `${diffInWeeks}w` : `${diffInWeeks} weeks`
    }
  } else if (diffInDays > 0) {
    if (diffInDays === 1) {
      strForTimestamp = abbreviated ? `${diffInDays}d` : `${diffInDays} day`
    } else {
      strForTimestamp = abbreviated ? `${diffInDays}d` : `${diffInDays} days`
    }
  } else if (diffInHours > 0) {
    if (diffInHours === 1) {
      strForTimestamp = abbreviated ? `${diffInHours}h` : `${diffInHours} hour`
    } else {
      strForTimestamp = abbreviated ? `${diffInHours}h` : `${diffInHours} hours`
    }
  } else if (diffInMinutes > 0) {
    if (diffInMinutes === 1) {
      strForTimestamp = abbreviated
        ? `${diffInMinutes}m`
        : `${diffInMinutes} minute`
    } else {
      strForTimestamp = abbreviated
        ? `${diffInMinutes}m`
        : `${diffInMinutes} minutes`
    }
  } else if (diffInSeconds === 1) {
    strForTimestamp = abbreviated
      ? `${diffInSeconds}s`
      : `${diffInSeconds} second`
  } else {
    strForTimestamp = abbreviated
      ? `${diffInSeconds}s`
      : `${diffInSeconds} seconds`
  }

  return strForTimestamp
}

export function getTimeSinceTimestampStr(
  timestamp: number,
  abbreviated: boolean = true,
): string {
  return getTimeBetweenTimestampsStr(
    timestamp,
    new Date().getTime(),
    abbreviated,
  )
}

export function getRelativeTimestampStr(timestamp) {
  let strForTimestamp
  const diffInMilliseconds = Math.abs(Date.now() - timestamp)

  const diffInDays = Math.floor(diffInMilliseconds / dayInMs)
  const diffInWeeks = Math.floor(diffInDays / 7)
  const diffInMonths = Math.floor(diffInDays / 30)
  const diffInYears = Math.floor(diffInDays / 365)

  if (diffInYears > 0) {
    strForTimestamp = `${diffInYears}y ago`
  } else if (diffInMonths > 0) {
    strForTimestamp = `${diffInMonths}mo ago`
  } else if (diffInWeeks > 0) {
    strForTimestamp = `${diffInWeeks}w ago`
  } else if (diffInDays > 0) {
    strForTimestamp = `${diffInDays}d ago`
  } else {
    strForTimestamp = 'today'
  }

  return strForTimestamp
}

export function isMoreThanNumMonthsAgo(
  timestamp,
  numMonths,
  fromTimestamp = Date.now(),
) {
  // A negative timestamp (typically a lastReviewedTimestamp value of '-1')
  // should be treated as a null value and not be used for computation here.
  // For the purposes of this function, it is not more than numMonths ago.
  if (timestamp === -1) {
    return false
  }
  const diffInMilliseconds = Math.max(fromTimestamp - timestamp, 0)
  const diffInDays = Math.floor(diffInMilliseconds / dayInMs)

  const diffInMonths = Math.floor(diffInDays / 30)

  return diffInMonths >= numMonths
}

export function isMoreThanNumDaysAgo(timestamp, numDays) {
  const diffInMilliseconds = Math.abs(Date.now() - timestamp)
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24))

  return diffInDays >= numDays
}

// Returns an array of numbers representing the months that have already
// transpired within the provided year
export function getElapsedMonthsByYear(year: string): number[] {
  const currentYear = new Date().getFullYear()

  if (year === currentYear.toString()) {
    const currentMonth = new Date().getMonth()
    return [...Array(currentMonth + 1).keys()].map((i) => i)
  }

  // all months
  return [...Array(12).keys()].map((i) => i)
}

export function getRemainingMonthsInYear(sinceMonth: number): number[] {
  // sinceMonth is 0-indexed, get all 0-indexed month indexes after sinceMonth
  return Array.from({ length: 12 - sinceMonth }, (_, i) => i + sinceMonth)
}

export enum OLDER_THAN_OPTION {
  NOW = 'all',
  ONE_MONTH = '1',
  TWO_MONTHS = '2',
  THREE_MONTHS = '3',
  CHOOSE_MONTH_YEAR = 'custom',
}

export function getOlderThanMonthYear(
  dateOption: OLDER_THAN_OPTION,
  sinceMonth: string,
  sinceYear: string,
): [number, number] {
  const currentDate = new Date()

  switch (dateOption) {
    case OLDER_THAN_OPTION.NOW:
      currentDate.setMonth(currentDate.getMonth() + 1)
      break
    case OLDER_THAN_OPTION.ONE_MONTH:
      currentDate.setDate(0)
      break
    case OLDER_THAN_OPTION.TWO_MONTHS:
      currentDate.setMonth(currentDate.getMonth() - 2)
      break
    case OLDER_THAN_OPTION.THREE_MONTHS:
      currentDate.setMonth(currentDate.getMonth() - 3)
      break
    default:
      currentDate.setMonth(currentDate.getMonth() + 1)
      break
  }

  if (dateOption !== OLDER_THAN_OPTION.CHOOSE_MONTH_YEAR) {
    return [currentDate.getMonth(), currentDate.getFullYear()]
  }

  return [parseInt(sinceMonth, 10), parseInt(sinceYear, 10)]
}
